import React from "react";
import { Outlet, useLocation, Navigate } from "react-router-dom";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import SEO from "@/components/SEO";
import { CheckCircle2 } from "lucide-react";

/**
 * Phase A — Universal Collector Onboarding shell.
 *
 * Renders the progress rail and a step-boundary card. Every onboarding
 * screen composes into this Layout.
 *
 * The layout enforces the onboarding gate: an unauthenticated visitor
 * is bounced to /login; a collector without an `in_progress` onboarding
 * state is sent to /dashboard (they don't belong here).
 */
const STEPS = [
  { id: "profile", label: "Profile" },
  { id: "albums", label: "Your Albums" },
];

export default function OnboardingLayout() {
  const { collector, loading } = useCollectorAuth();
  const location = useLocation();

  if (loading) {
    return <div className="p-10 text-center text-[var(--smoke)]" data-testid="onboarding-loading">Loading…</div>;
  }
  if (!collector) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  const ob = collector.onboarding || {};
  const currentStep = ob.current_step || "welcome";
  // Universal-onboarding gate:
  //   * `in_progress` — the collector belongs here.
  //   * `complete`    — allow ONLY the congrats screen (the celebratory
  //     "you're done" page needs to render once even after the state
  //     machine flips complete). All other steps redirect to /dashboard.
  //   * `not_started` — legacy account; they don't belong here. Send
  //     them to /dashboard.
  const isCongratsPath = location.pathname.endsWith("/congrats");
  if (ob.status === "in_progress") {
    // ok — render
  } else if (ob.status === "complete" && isCongratsPath) {
    // ok — allow the celebratory page
  } else {
    return <Navigate to="/dashboard" replace />;
  }

  const currentIdx = Math.max(0, STEPS.findIndex((s) => s.id === currentStep));

  return (
    <div className="min-h-screen bg-[var(--panini-yellow-soft)]/40" data-testid="onboarding-layout">
      <SEO title="Set up your CollectorUp account" />

      {/* Progress rail */}
      <div className="border-b-2 border-[var(--ink)] bg-white">
        <div className="container-gm py-5">
          <div className="flex items-center justify-between gap-2 overflow-x-auto" data-testid="onboarding-progress">
            {STEPS.map((s, i) => {
              const done = i < currentIdx;
              const active = i === currentIdx;
              return (
                <div
                  key={s.id}
                  data-testid={`onboarding-step-dot-${s.id}`}
                  data-active={active ? "true" : "false"}
                  data-done={done ? "true" : "false"}
                  className="flex items-center gap-2 shrink-0"
                >
                  <div
                    className={`flex items-center justify-center h-8 w-8 rounded-full border-2 border-[var(--ink)] font-bold text-xs ${
                      done
                        ? "bg-[var(--panini-red)] text-white"
                        : active
                        ? "bg-[var(--panini-yellow)] text-[var(--ink)]"
                        : "bg-white text-[var(--smoke)]"
                    }`}
                  >
                    {done ? <CheckCircle2 size={16} /> : i + 1}
                  </div>
                  <span
                    className={`hidden sm:inline text-sm ${
                      active ? "font-black text-[var(--ink)]" : "text-[var(--smoke)]"
                    }`}
                  >
                    {s.label}
                  </span>
                  {i < STEPS.length - 1 && <div className="hidden sm:block w-6 h-[2px] bg-[var(--ink)]/20" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Step-boundary content */}
      <div className="container-gm py-10 md:py-14 max-w-3xl">
        <Outlet />
      </div>
    </div>
  );
}
